<template>
  <div
    class="loads-carousel flex flex-col gap-1 rounded-lg text-left"
    :class="{
      'mx-6 bg-white p-4 shadow-md': isDesktop,
      'ml-6 mt-2 bg-transparent': !isDesktop,
    }"
  >
    <div class="flex items-center justify-between">
      <p class="text-xl text-steel-900">
        {{ isDesktop ? 'Recommended loads for you' : 'Recommended loads' }}
      </p>

      <div
        v-if="!isLoadsLoading"
        class="mr-3 flex gap-4 md:mr-0"
      >
        <div
          v-if="showPagination"
          class="flex rounded-lg border border-steel-300"
        >
          <img
            class="rotate-180 p-1"
            :class="{
              'opacity-40': currentItem === 0,
              'cursor-pointer': currentItem > 0,
            }"
            src="@/assets/chevron-right.svg"
            alt="previous"
            @click="previousItem"
          />

          <img
            class="cursor-pointer border-l border-steel-300 p-1 pr-[6px]"
            src="@/assets/chevron-right.svg"
            alt="next"
            @click="nextItem"
          />
        </div>

        <ButtonComponent
          class="border border-steel-300"
          @click="viewAllRedirect"
        >
          View All
        </ButtonComponent>
      </div>
    </div>

    <div
      v-if="isLoadsLoading"
      class="flex h-[250px] items-center justify-center"
    >
      <img
        class="loading m-auto h-12 w-12"
        src="@/assets/loading-v2-yellow.svg"
        alt="loading spinner"
      />
    </div>

    <div
      v-else-if="loads?.length === 0"
      class="mt-2 flex h-[210px] w-full items-center justify-center bg-steel-100"
    >
      <p class="text-base text-steel-800">
        We don't have any matches for you right now.
        <br />
        Check back soon for new recommendations.
      </p>
    </div>

    <Carousel
      v-else
      ref="loadsCarouselRef"
      v-model="currentItem"
      v-bind="config"
    >
      <Slide
        v-if="!hasAccessToExchange"
        class="min-w-[340px] max-w-[340px]"
      >
        <JoinTfxComponent></JoinTfxComponent>
      </Slide>
      <Slide
        v-for="load in loads"
        :key="load.id"
        class="min-w-[315px] max-w-[315px]"
        :class="{
          'blur-[3px]': !hasAccessToExchange,
        }"
      >
        <LoadDetail :load="load"></LoadDetail>
      </Slide>

      <template #addons>
        <Pagination
          v-if="!isDesktop"
          :paginate-items-to-show="true"
        />
      </template>
    </Carousel>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import 'vue3-carousel/dist/carousel.css';
import { Carousel, Slide, Pagination } from 'vue3-carousel';
import { LoadDetail, JoinTfxComponent, ButtonComponent } from '@/components';
import { useScreenSize } from '@/composables';

const store = useStore();
const { screenSize } = useScreenSize();

defineProps({
  viewAllRedirect: Function,
});

const userLoadBoard = computed(
  () => store.state.users?.userLoadBoardAccess?.user
);

const hasAccessToExchange = computed(() => {
  return (
    userLoadBoard.value?.has_active_coi_on_file &&
    userLoadBoard.value?.has_completed_idv &&
    userLoadBoard.value?.has_eld_connected_and_active &&
    (userLoadBoard.value?.cargo_policy_on_file_and_active ||
      userLoadBoard.value?.has_pending_cargo_policy_request)
  );
});

const loads = computed(() => {
  const results = store.state.loads?.recommended_loads?.loads;

  if (hasAccessToExchange.value) {
    return results;
  } else {
    return results?.slice(0, 3);
  }
});

const isLoadsLoading = computed(
  () => store.state.loads?.is_recommended_loads_loading
);
const isDesktop = computed(() => screenSize.value === 'desktop');

const loadsCarouselRef = ref();
const currentItem = ref(0);
const config = {
  height: 200,
  itemsToShow: isDesktop.value ? 4 : 1,
  itemsToScroll: 1,
  snapAlign: isDesktop.value ? 'start' : 'center',
  ...(hasAccessToExchange.value ? { mouseWheel: true } : {}),
};

const showPagination = computed(
  () =>
    !isLoadsLoading.value &&
    hasAccessToExchange.value &&
    isDesktop.value &&
    loads.value?.length > 4
);

const previousItem = () => loadsCarouselRef.value.prev();
const nextItem = () => loadsCarouselRef.value.next();
</script>

<style>
.loads-carousel .carousel__track {
  @apply gap-4 !pt-2;
}
</style>
